import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tooltip, Table, message, Button } from "antd";
import { ReloadOutlined, CopyOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { Blocks, Wallet, Coins, TrendingUp, Activity, Zap, Boxes } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import blockchainApi from "../../api/blockchainApi";
import AdminControls from "./AdminControls";
import "./blockchainDashboard.css";

const ROLE_TONE = {
    deployer: "border-rose-400/35 bg-rose-500/12 text-rose-100",
    signer: "border-amber-400/35 bg-amber-500/12 text-amber-100",
    platform: "border-sky-400/35 bg-sky-500/12 text-sky-100",
    promoter: "border-emerald-400/35 bg-emerald-500/12 text-emerald-100",
    user: "border-cyan-400/35 bg-cyan-500/12 text-cyan-100",
};

const STATUS_TONE = {
    pending_fee: "border-amber-400/35 bg-amber-500/12 text-amber-100",
    pending_deposit: "border-yellow-400/35 bg-yellow-500/12 text-yellow-100",
    funded: "border-emerald-400/35 bg-emerald-500/12 text-emerald-100",
    claiming: "border-sky-400/35 bg-sky-500/12 text-sky-100",
    completed: "border-violet-400/35 bg-violet-500/12 text-violet-100",
    withdrawn: "border-white/15 bg-white/[0.08] text-white/65",
    unknown: "border-white/15 bg-white/[0.08] text-white/65",
};

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const short = (addr) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—");

const formatNum = (n) => {
    const v = parseFloat(n);
    if (!isFinite(v)) return "0";
    if (v === 0) return "0";
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
    if (v >= 1_000) return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
    if (v >= 1) return v.toFixed(2);
    return v.toFixed(4);
};

const copyAddr = (addr) => {
    navigator.clipboard.writeText(addr);
    message.success("Скопировано");
};

function Pill({ children, className }) {
    return (
        <span
            className={cn(
                "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                className
            )}
        >
            {children}
        </span>
    );
}

// ─── KPI hero card ────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, hint, accent, balances, source, sourceAddr }) {
    return (
        <div className={cn(shellClass, "p-4 sm:p-5")}>
            <div className={cn("pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full blur-3xl", accent.glow)} />
            <div className="relative">
                <div className="flex items-start justify-between gap-3">
                    <span className={cn("flex h-10 w-10 items-center justify-center rounded-xl border", accent.iconBg)}>
                        <Icon className={cn("h-5 w-5", accent.iconColor)} strokeWidth={2} />
                    </span>
                    {source && (
                        <Tooltip title={sourceAddr || ""}>
                            <button
                                type="button"
                                onClick={() => sourceAddr && copyAddr(sourceAddr)}
                                className="text-right text-[10px] uppercase tracking-wider text-white/40 transition hover:text-white/75"
                            >
                                {source}
                                <span className="block font-mono text-[11px] normal-case">
                                    {short(sourceAddr)} {sourceAddr && <CopyOutlined className="text-[9px] opacity-50" />}
                                </span>
                            </button>
                        </Tooltip>
                    )}
                </div>

                <p className="mt-3 text-[12px] font-medium uppercase tracking-wide text-white/45">{label}</p>

                {balances.length === 0 ? (
                    <p className={cn("mt-1 text-2xl font-bold tabular-nums", accent.valueColor)}>0</p>
                ) : (
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        {balances.map((b) => (
                            <span key={b.symbol} className="flex items-baseline gap-1">
                                <span className={cn("text-2xl font-bold tabular-nums", accent.valueColor)}>{formatNum(b.balance)}</span>
                                <span className="text-[12px] font-medium text-white/45">{b.symbol}</span>
                            </span>
                        ))}
                    </div>
                )}

                {hint && <p className="mt-2 text-[12px] text-white/40">{hint}</p>}
            </div>
        </div>
    );
}

// ─── Money location card (where it sits) ──────────────────────────────────────
function MoneyLocationCard({ name, kind, address, ethBalance, tokenBalances, badge }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
            <div className="mb-3 flex items-start justify-between gap-2 border-b border-white/[0.07] pb-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-white/95">{name}</span>
                        {badge}
                    </div>
                    <p className="text-[11px] text-white/40">{kind}</p>
                </div>
                <Tooltip title={address}>
                    <button
                        type="button"
                        onClick={() => copyAddr(address)}
                        className="shrink-0 text-right font-mono text-[11px] text-white/45 transition-colors hover:text-white/85"
                    >
                        {short(address)} <CopyOutlined className="text-[10px]" />
                    </button>
                </Tooltip>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-wide text-white/35">ETH</span>
                    <span className="font-mono text-[14px] text-amber-200/95">{formatNum(ethBalance)}</span>
                </div>
                {tokenBalances?.map((tb) => (
                    <div key={tb.symbol} className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-wide text-white/35">{tb.symbol}</span>
                        <span
                            className={cn(
                                "font-mono text-[14px]",
                                parseFloat(tb.balance) > 0 ? "text-emerald-300/95" : "text-white/30"
                            )}
                        >
                            {formatNum(tb.balance)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Collapsible section ──────────────────────────────────────────────────────
function Collapsible({ icon: Icon, title, count, children }) {
    const [open, setOpen] = useState(false);
    return (
        <section className={cn(shellClass, "overflow-hidden")}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-white/[0.03] sm:px-5"
            >
                <div className="flex items-center gap-3">
                    {Icon && (
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06]">
                            <Icon className="h-4 w-4 text-white/65" strokeWidth={2} />
                        </span>
                    )}
                    <span className="text-[13px] font-semibold uppercase tracking-wide text-white/75">{title}</span>
                    {count != null && (
                        <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/55">
                            {count}
                        </span>
                    )}
                </div>
                <span className="text-white/40">{open ? <UpOutlined /> : <DownOutlined />}</span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-white/[0.06] px-4 py-4 sm:px-5">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function BlockchainDashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const handleSyncFunded = async () => {
        setSyncing(true);
        try {
            const res = await blockchainApi.syncFunded();
            const result = res.data || res;
            if (result.count > 0) {
                message.info(`${result.count} funded промо ожидают ручного запуска: #${result.ids?.join(", #")}`);
            } else {
                message.success("Все funded промо запущены");
            }
        } catch (e) {
            message.error("Ошибка проверки");
        } finally {
            setSyncing(false);
        }
    };

    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const res = await blockchainApi.getDashboard();
            setData(res.data || res);
        } catch (e) {
            console.error("Dashboard fetch error:", e);
            message.error("Не удалось загрузить blockchain dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    // ─── Derived KPIs ────────────────────────────────────────────────────────
    const kpis = useMemo(() => {
        if (!data) return null;
        const contracts = data.contracts || [];
        const accounts = data.accounts || [];

        const giveawayPool = contracts.find((c) => /GiveawayPool/i.test(c.name));
        const feeCollector = contracts.find((c) => /FeeCollector/i.test(c.name));
        const platformAcct = accounts.find((a) => a.role === "platform");

        // Сумма ненулевых токенов на контракте/аккаунте
        const nonZero = (tokenBalances) =>
            (tokenBalances || []).filter((t) => parseFloat(t.balance) > 0);

        // Кошелёк платформы: на деве он приходит среди hardhat-аккаунтов (role=platform),
        // на мейннете — отдельной записью PlatformWallet в contracts. Именно там лежат
        // заработанные комиссии: FeeCollector их не хранит, а сразу пересылает дальше.
        const platformWallet = contracts.find((c) => /PlatformWallet/i.test(c.name));

        // Fees — собираем из FeeCollector и кошелька платформы (по ненулевым символам)
        const feeMap = new Map();
        nonZero(feeCollector?.tokenBalances).forEach((t) => {
            feeMap.set(t.symbol, (feeMap.get(t.symbol) || 0) + parseFloat(t.balance));
        });
        nonZero(platformAcct?.tokenBalances).forEach((t) => {
            feeMap.set(t.symbol, (feeMap.get(t.symbol) || 0) + parseFloat(t.balance));
        });
        nonZero(platformWallet?.tokenBalances).forEach((t) => {
            feeMap.set(t.symbol, (feeMap.get(t.symbol) || 0) + parseFloat(t.balance));
        });
        const feeBalances = Array.from(feeMap.entries()).map(([symbol, balance]) => ({
            symbol,
            balance: balance.toString(),
        }));

        return {
            giveawayPool,
            feeCollector,
            platformAcct,
            heldBalances: nonZero(giveawayPool?.tokenBalances),
            feeBalances,
            feeSourceAddr: platformAcct?.address || feeCollector?.address,
        };
    }, [data]);

    const promoBuckets = useMemo(() => {
        if (!data) return null;
        const byStatus = data.promos?.byStatus || {};
        return {
            funded: byStatus.funded || 0,
            pending_fee: byStatus.pending_fee || 0,
            pending_deposit: byStatus.pending_deposit || 0,
            claiming: byStatus.claiming || 0,
            completed: byStatus.completed || 0,
            withdrawn: byStatus.withdrawn || 0,
            unknown: byStatus.unknown || 0,
            total: data.promos?.total || 0,
            claims: data.claims?.total || 0,
        };
    }, [data]);

    if (loading && !data) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex flex-1 flex-col items-center justify-center p-12 text-center")}>
                    <Blocks className="mb-3 h-14 w-14 text-white/25" strokeWidth={1.25} />
                    <p className="text-[14px] text-white/55">Не удалось загрузить данные</p>
                    <p className="mt-1 max-w-md text-[13px] text-white/40">Проверьте, что Hardhat / RPC доступны.</p>
                    <Button
                        type="primary"
                        onClick={fetchDashboard}
                        className="!mt-6 !rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        Повторить
                    </Button>
                </section>
            </div>
        );
    }

    const network = data.network || {};
    const safeAccounts = data.accounts || [];
    const safeContracts = data.contracts || [];
    const safePromos = data.promos?.list || [];

    const accountColumns = [
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            width: 90,
            render: (role) => (
                <Pill className={ROLE_TONE[role] || "border-white/15 bg-white/[0.08] text-white/65"}>{role}</Pill>
            ),
        },
        { title: "Label", dataIndex: "label", key: "label", width: 140 },
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            width: 180,
            render: (addr) => (
                <span className="flex items-center gap-1 font-mono text-[12px] text-white/65">
                    <Tooltip title={addr}>
                        <span>{short(addr)}</span>
                    </Tooltip>
                    <CopyOutlined
                        className="cursor-pointer text-white/35 transition-colors hover:text-white/80"
                        onClick={() => copyAddr(addr)}
                    />
                </span>
            ),
        },
        {
            title: "ETH",
            dataIndex: "ethBalance",
            key: "eth",
            width: 130,
            render: (v) => <span className="font-mono text-[13px] text-amber-200/95">{formatNum(v)}</span>,
        },
        ...(safeAccounts[0]?.tokenBalances || []).map((_, i) => ({
            title: safeAccounts[0].tokenBalances[i]?.symbol || `Token ${i}`,
            key: `token_${i}`,
            width: 130,
            render: (_, record) => {
                const tb = record.tokenBalances?.[i];
                return <span className="font-mono text-[13px] text-cyan-200/90">{tb ? formatNum(tb.balance) : "0"}</span>;
            },
        })),
    ];

    const promoColumns = [
        { title: "ID", dataIndex: "id", key: "id", width: 60 },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 200,
            render: (t) => <span className="block max-w-[180px] truncate text-white/90">{t || "—"}</span>,
        },
        {
            title: "Status",
            dataIndex: "blockchainStatus",
            key: "status",
            width: 120,
            render: (s) => (
                <Pill className={cn("!normal-case", STATUS_TONE[s] || "border-white/15 bg-white/[0.08] text-white/65")}>
                    {s || "—"}
                </Pill>
            ),
        },
        {
            title: "Promoter",
            dataIndex: "promoterAddress",
            key: "promoter",
            width: 140,
            render: (a) =>
                a ? (
                    <Tooltip title={a}>
                        <span className="font-mono text-[12px] text-white/65">{short(a)}</span>
                    </Tooltip>
                ) : (
                    "—"
                ),
        },
        {
            title: "Fee TX",
            dataIndex: "feeTxHash",
            key: "feeTx",
            width: 120,
            render: (h) =>
                h ? (
                    <Tooltip title={h}>
                        <Pill className="border-emerald-400/35 bg-emerald-500/12 text-emerald-100 normal-case">
                            {short(h)}
                        </Pill>
                    </Tooltip>
                ) : (
                    <Pill className="border-rose-400/35 bg-rose-500/12 text-rose-100">none</Pill>
                ),
        },
        {
            title: "Deposit TX",
            dataIndex: "depositTxHash",
            key: "depositTx",
            width: 120,
            render: (h) =>
                h ? (
                    <Tooltip title={h}>
                        <Pill className="border-emerald-400/35 bg-emerald-500/12 text-emerald-100 normal-case">
                            {short(h)}
                        </Pill>
                    </Tooltip>
                ) : (
                    <Pill className="border-rose-400/35 bg-rose-500/12 text-rose-100">none</Pill>
                ),
        },
    ];

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            {/* Header */}
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-violet-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Blocks className="h-5 w-5 text-violet-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Blockchain
                            </h1>
                            <p className="text-[12px] text-white/45">
                                Призовой пул, заработанные комиссии, состояние контрактов
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {promoBuckets?.funded > 0 && (
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Button
                                    onClick={handleSyncFunded}
                                    loading={syncing}
                                    className="!rounded-xl !border-emerald-400/30 !bg-emerald-500/10 !text-emerald-300 hover:!border-emerald-400/50 hover:!bg-emerald-500/20 disabled:!opacity-50"
                                >
                                    {promoBuckets.funded} ⛓ funded ждут запуска
                                </Button>
                            </motion.div>
                        )}
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                icon={<ReloadOutlined spin={loading} />}
                                onClick={fetchDashboard}
                                disabled={loading}
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10 disabled:!opacity-50"
                            >
                                Обновить
                            </Button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Админские on-chain действия: аварийный стоп, заморозка промо, окно клейма */}
            <AdminControls paused={data?.contractPaused} />

            {/* KPIs row 1: held in pool & fees earned */}
            {kpis && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <KpiCard
                        icon={Coins}
                        label="Призовой пул (held for prizes)"
                        hint="Депозиты промоутеров — выплачиваются победителям при claim"
                        accent={{
                            glow: "bg-emerald-500/15",
                            iconBg: "border-emerald-400/30 bg-emerald-500/15",
                            iconColor: "text-emerald-300",
                            valueColor: "text-emerald-300",
                        }}
                        balances={kpis.heldBalances}
                        source="GiveawayPool"
                        sourceAddr={kpis.giveawayPool?.address}
                    />
                    <KpiCard
                        icon={TrendingUp}
                        label="Заработано на комиссиях"
                        hint="Накоплено на платформе из платных fee'ов"
                        accent={{
                            glow: "bg-amber-500/15",
                            iconBg: "border-amber-400/30 bg-amber-500/15",
                            iconColor: "text-amber-300",
                            valueColor: "text-amber-300",
                        }}
                        balances={kpis.feeBalances}
                        source={kpis.platformAcct ? "Platform wallet" : "FeeCollector"}
                        sourceAddr={kpis.feeSourceAddr}
                    />
                </div>
            )}

            {/* KPIs row 2: contract state & network */}
            {kpis && promoBuckets && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {/* Contract state */}
                    <div className={cn(shellClass, "p-4 sm:p-5")}>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-400/30 bg-sky-500/15">
                                <Activity className="h-5 w-5 text-sky-300" strokeWidth={2} />
                            </span>
                            <div>
                                <p className="text-[12px] font-medium uppercase tracking-wide text-white/45">
                                    Состояние смарт-контракта
                                </p>
                                <p className="text-[12px] text-white/40">
                                    Всего on-chain промо: <span className="text-white/75">{promoBuckets.total}</span>
                                </p>
                            </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {Object.entries({
                                funded: promoBuckets.funded,
                                claiming: promoBuckets.claiming,
                                pending_fee: promoBuckets.pending_fee,
                                pending_deposit: promoBuckets.pending_deposit,
                                completed: promoBuckets.completed,
                                withdrawn: promoBuckets.withdrawn,
                                unknown: promoBuckets.unknown,
                            })
                                .filter(([, v]) => v > 0)
                                .map(([status, count]) => (
                                    <span
                                        key={status}
                                        className={cn(
                                            "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-medium",
                                            STATUS_TONE[status] || "border-white/15 bg-white/[0.08] text-white/70"
                                        )}
                                    >
                                        {status}
                                        <span className="rounded bg-black/25 px-1 text-[10px] tabular-nums">{count}</span>
                                    </span>
                                ))}
                            <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/12 px-2.5 py-1 text-[11px] font-medium text-violet-100">
                                claims
                                <span className="rounded bg-black/25 px-1 text-[10px] tabular-nums">{promoBuckets.claims}</span>
                            </span>
                        </div>
                    </div>

                    {/* Network */}
                    <div className={cn(shellClass, "p-4 sm:p-5")}>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/[0.06]">
                                <Zap className="h-5 w-5 text-white/75" strokeWidth={2} />
                            </span>
                            <div className="min-w-0">
                                <p className="text-[12px] font-medium uppercase tracking-wide text-white/45">Сеть</p>
                                <p className="font-mono text-[12px] text-white/60 truncate">{network.rpcUrl || "—"}</p>
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-white/35">Chain ID</p>
                                <p className="text-[15px] font-semibold tabular-nums text-white/90">{network.chainId ?? "—"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-white/35">Block</p>
                                <p className="text-[15px] font-semibold tabular-nums text-sky-300/95">{network.blockNumber ?? "—"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-white/35">Gas</p>
                                <p className="text-[15px] font-semibold tabular-nums text-amber-200/95">
                                    {network.gasPrice?.split(" ")[0] || "—"}
                                    <span className="ml-1 text-[10px] text-white/35">gwei</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Where the money is */}
            {kpis && (
                <section className={cn(shellClass, "p-4 sm:p-5")}>
                    <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-white/75">
                        <Wallet className="h-4 w-4 text-white/55" strokeWidth={2} />
                        Где лежат деньги
                    </h2>
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                        {kpis.giveawayPool && (
                            <MoneyLocationCard
                                name={kpis.giveawayPool.name}
                                kind="Smart contract · prize pool"
                                address={kpis.giveawayPool.address}
                                ethBalance={kpis.giveawayPool.ethBalance}
                                tokenBalances={kpis.giveawayPool.tokenBalances}
                                badge={
                                    kpis.giveawayPool.promoCount != null && (
                                        <span className="rounded-full border border-emerald-400/30 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">
                                            {kpis.giveawayPool.promoCount} promos
                                        </span>
                                    )
                                }
                            />
                        )}
                        {kpis.feeCollector && (
                            <MoneyLocationCard
                                name={kpis.feeCollector.name}
                                kind="Smart contract · fees"
                                address={kpis.feeCollector.address}
                                ethBalance={kpis.feeCollector.ethBalance}
                                tokenBalances={kpis.feeCollector.tokenBalances}
                            />
                        )}
                        {kpis.platformAcct && (
                            <MoneyLocationCard
                                name={kpis.platformAcct.label || "Platform wallet"}
                                kind="EOA · platform revenue"
                                address={kpis.platformAcct.address}
                                ethBalance={kpis.platformAcct.ethBalance}
                                tokenBalances={kpis.platformAcct.tokenBalances}
                                badge={
                                    <span className="rounded-full border border-sky-400/30 bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-200">
                                        platform
                                    </span>
                                }
                            />
                        )}
                    </div>
                </section>
            )}

            {/* Collapsible: all wallets */}
            <Collapsible icon={Wallet} title="Все Hardhat-кошельки" count={safeAccounts.length}>
                <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                    <Table
                        dataSource={safeAccounts}
                        columns={accountColumns}
                        rowKey="address"
                        pagination={false}
                        size="small"
                        scroll={{ x: 800 }}
                        className="bcd-scope pagination-custom"
                    />
                </div>
            </Collapsible>

            {/* Collapsible: blockchain promos table */}
            {safePromos.length > 0 && (
                <Collapsible icon={Boxes} title="On-chain промо (детально)" count={safePromos.length}>
                    <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                        <Table
                            dataSource={safePromos}
                            columns={promoColumns}
                            rowKey="id"
                            pagination={{ pageSize: 10, className: "pagination-custom !mb-0 flex flex-wrap justify-end" }}
                            size="small"
                            scroll={{ x: 800 }}
                            className="bcd-scope"
                        />
                    </div>
                </Collapsible>
            )}
        </div>
    );
}
